import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {InvestmentType} from "@/integrations/supabase/investment.models.ts";
import logger from "@logger";


class InvestmentService extends BaseService {

    /**
     * Fetches all investment types.
     * @returns {Promise<InvestmentType[]>} A promise that resolves to an array of investment types.
     */
    async fetchInvestmentTypes(): Promise<InvestmentType[]> {
        logger.info('Fetching investment types');
        const { data, error } = await supabase
            .from('investment_types')
            .select('*')

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Fetches all pension investment types.
     * @returns {Promise<InvestmentType[]>} A promise that resolves to an array of pension investment types.
     */
    async fetchPensionInvestmentTypes(): Promise<InvestmentType[]> {
        logger.info('Fetching Pension investment types');
        const { data, error } = await supabase
            .from('investment_types')
            .select('*')
            .in('name', ['Government Pension Plan', 'Occupational Pension Plan', 'Individual Pension Plan', 'Pension Plan']);

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Fetches a specific investment type by name.
     * @param {string} name - The name of the investment type to fetch.
     * @returns {Promise<InvestmentType>} A promise that resolves to the investment type.
     */
    async fetchInvestmentTypeByName(name: string): Promise<InvestmentType> {
        logger.info('Fetching investment type by name');
        const { data, error } = await supabase
            .from('investment_types')
            .select('*')
            .eq('name', name)
            .single()

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Fetches a specific pension investment type by name.
     * @param {string} pensionType - The name of the pension investment type to fetch.
     * @returns {Promise<InvestmentType>} A promise that resolves to the pension investment type.
     */
    async fetchPensionInvestmentType(pensionType: PensionPlanType): Promise<InvestmentType> {
        logger.info(`Fetching Pension investment types for pension type ${pensionType}`);
        const pensionTypeNames = {
            government: 'Government Pension Plan',
            occupational: 'Occupational Pension Plan',
            individual: 'Individual Pension Plan'
        };

        const { data, error } = await supabase
            .from('investment_types')
            .select('*')
            .eq('name', pensionTypeNames[pensionType])
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

}

export default new InvestmentService();
